import { minimatch } from 'minimatch'
import { HostServices } from '@ally/federated-types'

const REMOVE_SLASHES = /\//g

type FeatureFlags = HostServices['featureFlags']

type RedirectConfig = {
  match(location: Location, featureFlags: FeatureFlags): boolean
  redirectURL(location: Location): string
}

/**
 * @typedef {Object} RedirectConfig
 * @property {function} match - Function that determines whether the current location matches any of the routes defined in the `routeMigrationObj` object.
 * @property {function} redirectURL - Function that constructs a redirect URL based on the current location pathname.
 */
export const autoRedirects: RedirectConfig = {
  /**
   * @param {string} location - The current location pathname.
   * @param {FeatureFlags} featureFlags - An object containing various feature flags.
   * @returns {boolean} Whether the current location matches any of the routes defined in the `routeMigrationObj` object.
   */
  match: (
    location: { search: string; pathname: string },
    featureFlags: FeatureFlags,
  ): boolean => {
    const routeMigrationObj = featureFlags.auto.variation(
      'ff-auto-remote-routes',
      {},
    )
    const targetRoute = location.pathname.replace(REMOVE_SLASHES, '')

    const matchingRoute = Object.keys(routeMigrationObj).find(remoteRoute => {
      // Determine target route is match within the any remote routes
      return minimatch(
        targetRoute,
        `*${remoteRoute.replace(REMOVE_SLASHES, '')}*`,
      )
    })

    return matchingRoute && routeMigrationObj[matchingRoute]
  },
  /**
   * @param {string} location - The current location pathname.
   * @returns {string} The constructed redirect URL based on the current location pathname.
   */
  redirectURL: (location: { search: string; pathname: string }): string => {
    const sp = new URLSearchParams(location.search)
    const id = sp.get('id')

    const targetRoute = `${location.pathname.replace(REMOVE_SLASHES, '')}`
    const autoPaymentRoute = '*/auto/payments/*'.replace(REMOVE_SLASHES, '')
    // Determine target route is match with the auto payment route
    if (id && minimatch(targetRoute, autoPaymentRoute)) {
      sp.delete('id')
      const restPath = location.pathname.replace(/^\/sso\/auto\/payments/, '')
      const restSearch = sp.toString()
      return `/auto/payments${id ? '/'.concat(id) : ''}${restPath}${
        restSearch ? '?'.concat(restSearch) : ''
      }`
    }
    return `${location.pathname.replace(/^\/sso/, '')}${location.search}`
  },
}
